import { Injectable, inject } from '@angular/core';
import {
  CONSENT_APP_CONFIG, TRACKING_CHECKOUT_CONFIG, TrackingCheckoutStatus, TrackingCheckoutStep,
  TrackingErrorCategory, TrackingService
} from 'analytics';

/**
 * Tracking Engine reports of the shared order wizard (contract §3:
 * `checkout_started`, `checkout_step`). Provided by OrderWizardComponent, so the
 * step components reach it through the element injector and inject it optionally:
 * used anywhere else (e.g. a brand's own funnel payment page) they stay silent.
 *
 * Silent as well unless the app provides TRACKING_CHECKOUT_CONFIG — the page id
 * belongs to the app, never to this library. Params are only step / status /
 * error_category (and plan / source_page_id on `checkout_started`): never an
 * amount, an e-mail, a name or a wallet address.
 */
@Injectable()
export class OrderWizardTracking {
  private tracking = inject(TrackingService);
  private config = inject(TRACKING_CHECKOUT_CONFIG, { optional: true });
  private appPageId = inject(CONSENT_APP_CONFIG, { optional: true })?.pageId ?? null;

  get enabled(): boolean { return !!this.config?.pageId; }

  step(step: TrackingCheckoutStep, status: TrackingCheckoutStatus, errorCategory?: TrackingErrorCategory | null): void {
    if (!this.config?.pageId) return;
    this.tracking.checkoutStep(this.config.pageId, step, status, errorCategory);
  }

  /**
   * `checkout_started`, once per app run (TrackingService dedups). A checkout page
   * the app showed earlier (e.g. fac3less's direct checkout) has already sent it,
   * so this only fires when the wizard is the first checkout screen. The plan comes
   * from the app's product-code map; a product outside it sends no plan.
   */
  started(productCodes: (string | null | undefined)[]): void {
    const cfg = this.config;
    if (!cfg?.pageId || this.tracking.checkoutStartedAlready) return;
    const plans = cfg.plans ?? {};
    const code = productCodes.find(c => !!c && !!plans[c]);
    const plan = code ? plans[code] : null;
    this.tracking.checkoutStarted(cfg.pageId, plan, cfg.sourcePageId ?? this.appPageId ?? cfg.pageId,
                                  { allowNoPlan: true });
  }
}

/**
 * Error of a gateway / payment-confirmation call: the backend answers a declined
 * charge with 422 (or 402), so on those endpoints that IS the decline.
 */
export function paymentErrorCategory(err: any): TrackingErrorCategory {
  const status: number | undefined = err?.status;
  if (status === 0) return 'network';
  if (status === 402 || status === 422 || /declin/i.test(String(err?.error?.code ?? err?.error?.error ?? ''))) {
    return 'payment_declined';
  }
  return 'other';
}

/** Any other failed call on the confirmation screen: unanswered = network, else other. */
export function confirmationErrorCategory(err: any): TrackingErrorCategory {
  return err?.status === 0 ? 'network' : 'other';
}
